import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { useClerkGate, useStableGetToken } from '@/hooks/useClerkGate';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { logDebug, logError, logWarn } from '@/lib/logger';
import { takeOrgFromLink } from '@/lib/org-link';
import { isIdentityQuery } from '@/lib/access-revision';
import { meResponseSchema } from '@aica/api-types/schemas';
import type { SubscriptionTier } from '@aica/api-types/schemas/stripe';

/**
 * Organization Context - Single Responsibility: Organization management
 * Handles org selection, switching, and persistence
 */

// UUID validation
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidOrgId = (id: string | null | undefined): id is string => {
  if (!id || typeof id !== 'string') return false;
  return UUID_REGEX.test(id);
};

// Debounce utility

function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  wait: number
): (...args: TArgs) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  return (...args: TArgs) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, wait);
  };
}

// Mapped result of /me (valid orgs + isOwner flag) used by the context.
interface UserData {
  organizations: Organization[];
  isOwner: boolean;
  /**
   * Состояние поп-апа «что нового» (#946) — едет с тем же `/me`, чтобы не
   * тратить второй запрос на два поля, нужных до первой отрисовки.
   */
  lastSeenRelease: string | null;
  userCreatedAt: string | null;
}

/**
 * Ошибка загрузки ростера организаций.
 *
 * `retriable` отвечает на вопрос «повтор имеет смысл?»: транспортный сбой
 * (обрыв сети, 5xx, тело-не-JSON от эджа) — да; осознанный отказ сервера
 * (403, 401, наш JSON с текстом) — нет. Корневой layout выбирает по этому
 * флагу между экраном «Повторить» и экраном «обратитесь к администратору».
 */
export class OrgFetchError extends Error {
  readonly retriable: boolean;

  constructor(message: string, retriable: boolean) {
    super(message);
    this.name = 'OrgFetchError';
    this.retriable = retriable;
  }
}

// Fetch user data from API (organizations + isOwner flag)
// Экспортируется, чтобы поведение загрузчика проверялось напрямую: гонять его
// через провайдер и TanStack Query ради проверки разбора ответа — лишний слой.
//
// `fresh` — мимо HTTP-кеша браузера. `/users/me` отдаётся с
// `Cache-Control: private, max-age=60`: обычный повтор в эту минуту браузер
// отвечает сам, не спросив сервер. Нужно после записи, которую ростер
// отражает (первый день недели, #864) — `cache: 'reload'` идёт на сервер и
// заодно обновляет запись кеша, чтобы следующий обычный запрос не отдал старое.
export async function fetchUserData(
  token: string | null,
  options: { fresh?: boolean } = {}
): Promise<UserData> {
  if (!token) {
    throw new OrgFetchError('No authentication token available', false);
  }

  const { API_BASE_URL } = await import('@/lib/api/config');

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
      ...(options.fresh ? { cache: 'reload' as const } : {}),
    });
  } catch {
    // fetch отвергает промис только на транспортном сбое — HTTP-статус
    // ошибкой не считается. Значит сюда попадает обрыв связи, и повтор уместен.
    throw new OrgFetchError('Network request failed', true);
  }

  // 404 = Clerk user exists but has no D1 record yet (organic signup, no invite).
  // Return an empty roster so MainAppLayout routes to /onboarding, where the
  // wizard provisions the user + workspace via POST /api/v1/onboarding/complete.
  if (res.status === 404) {
    return {
      organizations: [],
      isOwner: false,
      lastSeenRelease: null,
      userCreatedAt: null,
    };
  }

  if (!res.ok) {
    const errorData = (await res.json().catch(() => null)) as {
      message?: string;
      error?: string;
    } | null;
    // Тело не разобралось — значит отвечали не мы, а эдж (502/521/1101
    // отдают HTML). Такой сбой почти всегда секундный, повтор оправдан.
    // 5xx с нашим JSON тоже повторяем: это не отказ, а поломка.
    const retriable = errorData === null || res.status >= 500;
    throw new OrgFetchError(
      errorData?.message || errorData?.error || 'Failed to fetch organizations',
      retriable
    );
  }

  // Validate the /me payload against the SSOT schema (no blind as-cast).
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // 2xx with a body that isn't JSON — a captive portal or proxy serving an
    // HTML interstitial on a 200 status. Same transport-anomaly class as the
    // unparseable error body above, so it gets the same treatment: retriable.
    throw new OrgFetchError('Failed to parse organizations response', true);
  }
  const parsed = meResponseSchema.safeParse(body);
  if (!parsed.success) {
    logError('Invalid /users/me response shape', {
      component: 'OrganizationContext',
      issues: parsed.error.issues.map((i) => ({
        path: i.path.join('.'),
        message: i.message,
      })),
    });
    throw new OrgFetchError('Failed to fetch organizations', false);
  }
  const data = parsed.data;

  // Validate org IDs and log errors for invalid ones
  const allOrgs: Organization[] = data.orgs;
  const invalidOrgs = allOrgs.filter((org: Organization) => !isValidOrgId(org.id));

  // Log error if backend returned invalid org IDs (serious issue!)
  if (invalidOrgs.length > 0) {
    logError('Backend returned invalid org IDs', {
      component: 'OrganizationContext',
      invalidOrgs: invalidOrgs.map((org: Organization) => ({
        id: org.id,
        name: org.name,
      })),
      invalidCount: invalidOrgs.length,
      totalCount: allOrgs.length,
    });
  }

  const validOrgs = allOrgs.filter((org: Organization) => isValidOrgId(org.id));

  if (validOrgs.length === 0) {
    logWarn('No valid orgs from API', { component: 'OrganizationContext' });
  }

  return {
    organizations: validOrgs,
    isOwner: data.isOwner ?? false, // Platform super-admin flag
    // Состояние поп-апа «что нового» (#946) — из этого же ответа, чтобы не
    // делать второй запрос к /me ради двух полей на первом экране.
    lastSeenRelease: data.lastSeenRelease ?? null,
    userCreatedAt: data.userCreatedAt ?? null,
  };
}

export interface Organization {
  id: string;
  name: string;
  role: string;
  bitrix24Domain: string | null;
  /**
   * Тариф организации — известен КАЖДОЙ роли, а не только тем, у кого есть
   * `billing.read` (#978). `TierContext` для admin/agent/supervisor подписку не
   * запрашивает и отдаёт нейтральный `free`, то есть о тарифе врёт; когда тариф
   * нужен для утверждения, показанного человеку, брать его отсюда.
   */
  subscriptionTier?: SubscriptionTier;
  /**
   * Первый день недели организации (#864), ISO 1 = пн … 7 = вс — тоже для
   * КАЖДОЙ роли: `GET /api/v1/settings` у agent и supervisor отвечает 403.
   * Читает его `useWeekStartsOn` через `ClerkAuthProvider`.
   */
  weekStartsOn?: number;
}

type OrgState =
  | { status: 'initializing' }
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'ready'; orgs: Organization[] };

interface OrganizationContextValue {
  orgState: OrgState;
  organizations: Organization[];
  currentOrganization: Organization | null;
  selectedOrgId: string | null;
  selectOrganization: (orgId: string) => void;
  isLoading: boolean;
  error: string | null;
  /** Ошибка загрузки ростера повторяема (транспорт), а не отказ сервера. */
  isErrorRetriable: boolean;
  /** Повторить загрузку ростера — для экрана транзиентной ошибки. */
  retryUserData: () => void;
  // Org switching state for blocking UI during switch
  isOrgSwitching: boolean;
  // Platform super-admin flag (from /me endpoint)
  isOwner: boolean;
  // True once the /me fetch has settled (succeeded or errored) at least
  // once, so `isOwner` carries a confirmed value rather than its unresolved
  // default. Unlike `isLoading`, this has no gap around the query's
  // `enabled` flag flipping true — see the comment above `isLoading` below.
  isOwnerResolved: boolean;
  /** Последний релиз, поп-ап которого пользователь уже видел (#946). */
  lastSeenRelease: string | null;
  /** Когда пользователь появился — чтобы не показывать ему релиз до него. */
  userCreatedAt: string | null;
  /**
   * The email link named an organization the user cannot open (#1232), and the
   * reader must be told why: `not-a-member` — the app stayed on their own
   * selection; `no-organizations` — they have none at all (onboarding), so
   * there is no "own organization" to fall back to.
   */
  linkOrgDenied: OrgLinkDenial | null;
  /** The notice about {@link linkOrgDenied} has been shown. */
  dismissLinkOrgNotice: () => void;
}

export type OrgLinkDenial = 'not-a-member' | 'no-organizations';

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

const LOCAL_STORAGE_KEY = 'aica_selected_org_id';

// Switch overlay lifetime: held at least MIN (anti-flicker + time for the
// new org's queries to start), dismissed at MAX no matter what.
const MIN_SWITCH_OVERLAY_MS = 300;
const MAX_SWITCH_OVERLAY_MS = 3000;

// Ближайшая загрузка ростера идёт мимо кеша браузера (`fetchUserData`, `fresh`).
// Флаг снимается только после УДАЧНОЙ загрузки: повтор после сбоя тоже должен
// спросить сервер, а не взять старую запись.
let freshUserDataRequested = false;

/** После записи, которую отражает `/users/me`: следующая загрузка ростера — свежая. */
export function requestFreshUserData(): void {
  freshUserDataRequested = true;
}

/** Загрузка ростера для запроса `['user-data']`: свежая, если её запросили. */
export async function loadUserData(token: string | null): Promise<UserData> {
  const fresh = freshUserDataRequested;
  const data = await fetchUserData(token, { fresh });
  if (fresh) freshUserDataRequested = false;
  return data;
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isOrgSwitching, setIsOrgSwitching] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);
  const [linkOrgDenied, setLinkOrgDenied] = useState<OrgLinkDenial | null>(null);
  // The organization an email link named (`lib/org-link.ts`), taken once.
  // A ref, not a read inside the effect: StrictMode re-runs the effect, and a
  // second take would find storage empty and restore the saved org instead.
  // `undefined` = not taken yet; `null` = the page was not opened from a link.
  const linkOrgRef = useRef<string | null | undefined>(undefined);
  const switchStartTimeRef = useRef<number>(0);
  // Stable Clerk access — Clerk's useAuth() would re-render this provider
  // (and via the inline value, every consumer) on each ~50s token refresh.
  const { isLoaded: isClerkLoaded, isSignedIn } = useClerkGate();
  const getToken = useStableGetToken();
  const queryClient = useQueryClient();

  // Auto-cleanup localStorage on mount
  useEffect(() => {
    const savedOrgId = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedOrgId && !isValidOrgId(savedOrgId)) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      logWarn('Cleaned up invalid org ID from localStorage on mount', {
        component: 'OrganizationContext',
        invalidOrgId: savedOrgId,
      });
    }
  }, []);

  // Fetch user data (organizations + isOwner) using TanStack Query
  const {
    data: userData,
    isLoading: isQueryLoading,
    error: queryError,
    isSuccess,
    refetch,
  } = useQuery<UserData, Error>({
    queryKey: ['user-data'],
    queryFn: async () => {
      const token = await getToken();
      try {
        return await loadUserData(token);
      } catch (err) {
        logError(
          'Failed to fetch organizations',
          { component: 'OrganizationContext' },
          err instanceof Error ? err : undefined
        );
        throw err;
      }
    },
    enabled: isClerkLoaded && isSignedIn,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Extract organizations and isOwner from response - memoize to prevent infinite loops
  const organizations = useMemo(
    () => userData?.organizations ?? [],
    [userData?.organizations]
  );
  const isOwner = userData?.isOwner ?? false;
  const lastSeenRelease = userData?.lastSeenRelease ?? null;
  const userCreatedAt = userData?.userCreatedAt ?? null;

  // Restore or select org when organizations are loaded
  useEffect(() => {
    if (!isSuccess) return;

    // The organization an email link named (#1232) is taken once, here, and
    // spent right after it is used (`linkOrgRef = null`) in every branch below.
    // A later roster change — a refetch that comes back empty — must not find a
    // link that was already applied and report it as "no access".
    if (linkOrgRef.current === undefined) linkOrgRef.current = takeOrgFromLink();
    const linkOrgId = linkOrgRef.current;

    // A signed-in user with no organizations has no membership the link could
    // name: it is spent here (a stale id must not wait in sessionStorage for a
    // later roster) and the reader is told, on the onboarding screen they are
    // about to land on.
    if (organizations.length === 0) {
      if (linkOrgId) {
        linkOrgRef.current = null;
        setLinkOrgDenied('no-organizations');
        logWarn('Link names an organization for a user with no organizations', {
          component: 'OrganizationContext',
        });
      }
      return;
    }

    // Only proceed if selectedOrgId not already set (prevents multiple calls)
    if (selectedOrgId) return;

    // The link wins over the saved selection — but only from the user's own
    // roster: the parameter selects among memberships, it never grants one.
    if (linkOrgId) {
      linkOrgRef.current = null;
      if (organizations.some((o: Organization) => o.id === linkOrgId)) {
        setSelectedOrgId(linkOrgId);
        localStorage.setItem(LOCAL_STORAGE_KEY, linkOrgId);
        return;
      }
      setLinkOrgDenied('not-a-member');
      logWarn('Link names an organization the user is not a member of', {
        component: 'OrganizationContext',
      });
    }

    const savedOrgId = localStorage.getItem(LOCAL_STORAGE_KEY);
    const savedOrgExists =
      isValidOrgId(savedOrgId) &&
      organizations.find((o: Organization) => o.id === savedOrgId);

    if (savedOrgExists) {
      setSelectedOrgId(savedOrgId);
    } else {
      if (savedOrgId) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        logWarn('Saved org not found, falling back to first valid org', {
          component: 'OrganizationContext',
          savedOrgId,
        });
      }
      const firstOrgId = organizations[0]?.id;
      if (firstOrgId) {
        setSelectedOrgId(firstOrgId);
        localStorage.setItem(LOCAL_STORAGE_KEY, firstOrgId);
      }
    }
  }, [isSuccess, organizations, selectedOrgId]);

  // Internal org selection - now with blocking modal
  const selectOrgInternal = useCallback(
    (orgId: string) => {
      if (!isValidOrgId(orgId)) {
        logWarn('Invalid org ID format in selectOrganization', {
          component: 'OrganizationContext',
          orgId,
        });
        return;
      }

      // Don't switch if already on this org
      if (orgId === selectedOrgId) return;

      const previousOrgId = selectedOrgId;

      // Start switching - show blocking modal
      setIsOrgSwitching(true);
      setPendingOrgId(orgId);
      switchStartTimeRef.current = Date.now();

      // Cancel and remove every application-data query before changing the org.
      // Most query keys carry selectedOrgId, but this deny-by-default sweep also
      // protects against a future org-scoped query accidentally omitting it. The
      // identity roster is the only cache that is independent of org selection.
      if (previousOrgId) {
        void queryClient.cancelQueries({
          predicate: (query) => !isIdentityQuery(query.queryKey),
        });
        queryClient.removeQueries({
          predicate: (query) => !isIdentityQuery(query.queryKey),
        });
      }

      setSelectedOrgId(orgId);
      localStorage.setItem(LOCAL_STORAGE_KEY, orgId);
    },
    [selectedOrgId, queryClient]
  );

  // Complete the switch once no queries are in flight. New-org queries kick
  // off in the same commit that delivers the new selectedOrgId, so the short
  // minimum hold covers the gap before the first fetch registers — and keeps
  // the overlay from flickering when the new org's cache is still warm.
  // Imperative cache subscription, scoped to the switch window: useIsFetching
  // would re-render this top-level provider on every fetch transition
  // app-wide, all the time. (The predecessor — a confirmOrgSwitchComplete()
  // callback pages were meant to call — was never wired up, so every switch
  // used to sit out a fixed 3s timeout and log a warning.)
  useEffect(() => {
    if (!isOrgSwitching) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tryComplete = () => {
      if (queryClient.isFetching() > 0) {
        // Fetches (re)started — abandon any pending completion.
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
        return;
      }
      if (timeoutId !== undefined) return; // completion already scheduled

      const elapsed = Date.now() - switchStartTimeRef.current;
      timeoutId = setTimeout(
        () => {
          if (queryClient.isFetching() === 0) {
            setIsOrgSwitching(false);
            setPendingOrgId(null);
          } else {
            // Fetches resumed while we waited — the next cache event retries.
            timeoutId = undefined;
          }
        },
        Math.max(0, MIN_SWITCH_OVERLAY_MS - elapsed)
      );
    };

    const unsubscribe = queryClient.getQueryCache().subscribe(tryComplete);
    tryComplete();

    return () => {
      unsubscribe();
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [isOrgSwitching, queryClient]);

  // Hard cap: background pollers or a slow endpoint must not hold the
  // fullscreen overlay hostage. Reaching the cap is a normal outcome, not a
  // defect worth a Sentry event.
  useEffect(() => {
    if (!isOrgSwitching) return;

    const timeoutId = setTimeout(() => {
      setIsOrgSwitching(false);
      setPendingOrgId(null);
      logDebug('Org switch overlay dismissed by cap', {
        component: 'OrganizationContext',
        pendingOrgId,
      });
    }, MAX_SWITCH_OVERLAY_MS);

    return () => clearTimeout(timeoutId);
  }, [isOrgSwitching, pendingOrgId]);

  // Debounced selection
  const selectOrganization = useMemo(
    () => debounce(selectOrgInternal, 300),
    [selectOrgInternal]
  );

  // Computed values. No memo needed: find() returns a referentially stable
  // element while `organizations` (memoized) and `selectedOrgId` are stable.
  const currentOrganization = organizations.find((o) => o.id === selectedOrgId) || null;
  // IMPORTANT: Keep isLoading=true until selectedOrgId is set
  // This prevents race conditions where consumers see isLoading=false but selectedOrgId=null
  const isLoading =
    !isClerkLoaded ||
    isQueryLoading ||
    (isSuccess && organizations.length > 0 && !selectedOrgId);
  const error = queryError instanceof Error ? queryError.message : null;
  const isErrorRetriable =
    queryError instanceof OrgFetchError ? queryError.retriable : false;
  const retryUserData = useCallback(() => {
    void refetch();
  }, [refetch]);
  const dismissLinkOrgNotice = useCallback(() => setLinkOrgDenied(null), []);
  // TanStack Query v5 computes its own `isLoading` as `isPending && isFetching`.
  // The instant `enabled` (isClerkLoaded && isSignedIn) first flips true, the
  // fetch itself is kicked off from an effect that hasn't fired yet, so for
  // that one render `isFetching` (and thus `isLoading` above) reads false
  // even though no data has arrived — `isOwner`/`organizations` are still
  // their unresolved defaults. `isSuccess`/the query error have no such gap:
  // they only flip once the fetch has actually settled.
  const isOwnerResolved = isSuccess || Boolean(queryError);

  // Compute org state for backward compatibility
  const orgState: OrgState = useMemo(() => {
    if (!isClerkLoaded) return { status: 'initializing' };
    if (isQueryLoading) return { status: 'loading' };
    if (queryError) {
      const errorMessage =
        queryError instanceof Error
          ? queryError.message
          : 'Failed to fetch organizations';
      return { status: 'error', error: errorMessage };
    }
    return { status: 'ready', orgs: organizations };
  }, [isClerkLoaded, isQueryLoading, queryError, organizations]);

  // Memoized so the context identity only changes when org state actually
  // changes — an inline object here re-rendered all consumers on every
  // provider render (token refresh, parent updates).
  const value = useMemo(
    () => ({
      orgState,
      organizations,
      currentOrganization,
      selectedOrgId,
      selectOrganization,
      isLoading,
      error,
      isErrorRetriable,
      retryUserData,
      isOrgSwitching,
      isOwner,
      isOwnerResolved,
      lastSeenRelease,
      userCreatedAt,
      linkOrgDenied,
      dismissLinkOrgNotice,
    }),
    [
      orgState,
      organizations,
      currentOrganization,
      selectedOrgId,
      selectOrganization,
      isLoading,
      error,
      isErrorRetriable,
      retryUserData,
      isOrgSwitching,
      isOwner,
      isOwnerResolved,
      lastSeenRelease,
      userCreatedAt,
      linkOrgDenied,
      dismissLinkOrgNotice,
    ]
  );

  return (
    <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
  );
}

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context)
    throw new Error('useOrganization must be used within OrganizationProvider');
  return context;
};
